import TagManager, { DataLayerArgs } from "react-gtm-module";
import * as storage from "../helpers/storage";
import {
  InvoiceLineSpec,
  isAddonFeeInvoiceLine,
  isAddonFeeInvoiceLineSpec,
  isAddonProductInvoiceLine,
  isAddonProductInvoiceLineSpec,
  isTicketFeeInvoiceLine,
  isTicketFeeInvoiceLineSpec,
  isTicketInvoiceLine,
  isTicketInvoiceLineSpec,
  Order,
} from "../types";
import { getUtm } from "./utm";

const utmParams = getUtm();

const commonDataLayerParams = {
  organization_id: window.ORGANIZATION_ID,
  utm_content: utmParams.content,
  utm_campaign: utmParams.name,
  utm_medium: utmParams.medium,
  utm_source: utmParams.source,
  utm_term: utmParams.term,
};

const identifiersWithTrackers = [
  "haikjeften",
  "lillehammerlive",
  "verket",
  "sarpsborgfestivalen",
  "drobakfestivalen",
];

export const pushToDataLayer = (dataLayerArgs: DataLayerArgs) => {
  const dl: DataLayerArgs = { dataLayer: { ...dataLayerArgs.dataLayer, ...commonDataLayerParams } };
  TagManager.dataLayer(dl);
};

let pixelInitialized = false;
let orgTracker = false;
export const initialize = () => {
  TagManager.initialize({ gtmId: window.ENVIRONMENT.__HOOPLA_GTM__ });

  if (window.ORGANIZATION_GOOGLE_ANALYTICS) {
    pushToDataLayer({
      dataLayer: {
        event: "config_sales",
        enhanced_measurement: window.ORGANIZATION_ENHANCED_MEASUREMENT === "true",
        ga4_tracking_id: window.ORGANIZATION_GOOGLE_ANALYTICS,
      },
    });
    pushToDataLayer({
      dataLayer: {
        event: "config_sales_hoopla",
        ga4_hoopla_tracking_id: window.ENVIRONMENT.__HOOPLA_GA4__,
      },
    });
    orgTracker = true;
  } else {
    pushToDataLayer({
      dataLayer: {
        event: "config_sales_hoopla",
        ga4_hoopla_tracking_id: window.ENVIRONMENT.__HOOPLA_GA4__,
      },
    });
  }
  if (window.ORGANIZATION_FB_PIXEL) {
    pushToDataLayer({
      dataLayer: {
        event: "config_sales_fbp",
        fb_pixel_id: window.ORGANIZATION_FB_PIXEL,
      },
    });
    pixelInitialized = true;
  }
};

export const ecommerce = (order: Order) => {
  const previouslyTrackedPurchases = JSON.parse(storage.getItem("trackedPurchases", "[]"));
  if (previouslyTrackedPurchases.includes(order.orderId)) return;

  const ticketInvoiceLines = Object.groupBy(
    order.invoiceLines.filter(isTicketInvoiceLine),
    ({ ticket_type_id }) => ticket_type_id,
  );
  const ticketFeeInvoiceLines = Object.groupBy(
    order.invoiceLines.filter(isTicketFeeInvoiceLine),
    ({ ticket_type_id }) => ticket_type_id,
  );
  const addonLines = Object.groupBy(
    order.invoiceLines.filter(isAddonProductInvoiceLine),
    ({ addon_product_id }) => addon_product_id,
  );
  const addonFeeLines = Object.groupBy(
    order.invoiceLines.filter(isAddonFeeInvoiceLine),
    ({ addon_product_id }) => addon_product_id,
  );
  const notFoundTicket = { description: "", amount: 0 };
  const hooplaItems: object[] = [];
  const orgItems: object[] = [];
  Object.entries(ticketInvoiceLines).forEach(([ticketTypeId, tls]) => {
    const description = (tls![0] || notFoundTicket).description;
    const tfls = ticketFeeInvoiceLines[ticketTypeId] || [notFoundTicket];
    const ticket_price = (tls![0] || notFoundTicket).amount / 100;
    const price = ticket_price + tfls[0].amount / 100;
    hooplaItems.push({
      item_id: ticketTypeId,
      item_name: description,
      item_category: "Tickets + Ticket Fee",
      price: price,
      quantity: tls!.length || 0,
      affiliation: window.location.host.split(".")[0], // organization identifier
    });

    if (
      orgTracker ||
      identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
    ) {
      orgItems.push({
        item_id: ticketTypeId,
        item_name: description,
        item_category: "Tickets",
        price: ticket_price,
        quantity: tls!.length || 0,
      });
    }
  });

  Object.entries(addonLines).forEach(([addonProductId, tls]) => {
    const description = (tls![0] || notFoundTicket).description;
    const tfls = addonFeeLines[addonProductId] || [notFoundTicket];
    const ticket_price = (tls![0] || notFoundTicket).amount / 100;
    const price = ticket_price + tfls[0].amount / 100;
    hooplaItems.push({
      item_id: addonProductId,
      item_name: description,
      item_category: "Addons + Addon Fee",
      price: price,
      quantity: tls!.length || 0,
      affiliation: window.location.host.split(".")[0], // organization identifier
    });

    if (
      orgTracker ||
      identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
    ) {
      orgItems.push({
        item_id: addonProductId,
        item_name: description,
        item_category: "Addons",
        price: ticket_price,
        quantity: tls!.length || 0,
      });
    }
  });

  const hooplaTransaction = {
    transaction_id: order.orderId,
    value:
      order.invoiceLines
        .filter(
          (il) =>
            isTicketInvoiceLine(il) ||
            isTicketFeeInvoiceLine(il) ||
            isAddonFeeInvoiceLine(il) ||
            isAddonProductInvoiceLine(il),
        )
        .reduce((acc, il) => acc + il.amount, 0) / 100,
    tax:
      order.invoiceLines
        .filter(
          (il) =>
            isTicketInvoiceLine(il) ||
            isTicketFeeInvoiceLine(il) ||
            isAddonFeeInvoiceLine(il) ||
            isAddonProductInvoiceLine(il),
        )
        .reduce((acc, il) => acc + il.vat, 0) / 100,
    items: hooplaItems,
    currency: window.ORGANIZATION_CURRENCY,
    shipping: 0,
  };

  pushToDataLayer({
    dataLayer: {
      event: "complete_order",
      ga4_tracking_id: window.ENVIRONMENT.__HOOPLA_GA4__,
      ...hooplaTransaction,
    },
  });

  if (
    orgTracker ||
    identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
  ) {
    const transaction = {
      transaction_id: order.orderId,
      value:
        order.invoiceLines
          .filter((il) => isTicketInvoiceLine(il) || isAddonProductInvoiceLine(il))
          .reduce((acc, il) => acc + il.amount, 0) / 100,
      tax:
        order.invoiceLines
          .filter((il) => isTicketInvoiceLine(il) || isAddonProductInvoiceLine(il))
          .reduce((acc, il) => acc + il.vat, 0) / 100,
      items: orgItems,
      currency: window.ORGANIZATION_CURRENCY,
      shipping: 0,
    };

    pushToDataLayer({
      dataLayer: {
        event: "complete_order",
        ga4_tracking_id: window.ORGANIZATION_GOOGLE_ANALYTICS,
        ...transaction,
      },
    });
  }

  if (pixelInitialized) {
    const c: { id: string; item_price: number; quantity: number }[] = [];
    Object.values(ticketInvoiceLines).forEach((tls) => {
      const description = (tls![0] || notFoundTicket).description;
      const ticket_price = (tls![0] || notFoundTicket).amount / 100;
      c.push({
        id: description,
        item_price: ticket_price,
        quantity: tls!.length || 0,
      });
    });
    Object.values(addonLines).forEach((als) => {
      const description = (als![0] || notFoundTicket).description;
      const ticket_price = (als![0] || notFoundTicket).amount / 100;
      c.push({
        id: description,
        item_price: ticket_price,
        quantity: als!.length || 0,
      });
    });
    pushToDataLayer({
      dataLayer: {
        fb_pixel_id: window.ORGANIZATION_FB_PIXEL,
        event: "complete_order_fbp",
        value:
          order.invoiceLines
            .filter((il) => isTicketInvoiceLine(il) || isAddonProductInvoiceLine(il))
            .reduce((sum, il) => sum + il.amount, 0) / 100,
        currency: window.ORGANIZATION_CURRENCY,
        contents: c,
        content_type: "product",
        content_name: order.event.name,
        event_name: order.event.name,
        event_id: order.event.eventId,
        checkout_method: order.processor,
      },
    });
  }

  const trackedPurchases = [...previouslyTrackedPurchases, order.orderId];
  storage.setItem("trackedPurchases", JSON.stringify(trackedPurchases));
};

const groupBy = (arr: any[], keyFn: (k: any) => string): { [k: string]: any[] } => {
  return arr.reduce((acc, el) => {
    if (acc[keyFn(el)]) {
      acc[keyFn(el)].push(el);
    } else {
      acc[keyFn(el)] = [el];
    }
    return acc;
  }, {});
};

export const initCheckout = (lines: InvoiceLineSpec[]) => {
  const ticketInvoiceLines = groupBy(
    lines.filter(isTicketInvoiceLineSpec),
    ({ ticket_type_id }) => ticket_type_id,
  );
  const ticketFeeInvoiceLines = groupBy(
    lines.filter(isTicketFeeInvoiceLineSpec),
    ({ ticket_type_id }) => ticket_type_id,
  );
  const addonLines = groupBy(
    lines.filter(isAddonProductInvoiceLineSpec),
    ({ addon_product_id }) => addon_product_id,
  );
  const addonFeeLines = groupBy(
    lines.filter(isAddonFeeInvoiceLineSpec),
    ({ addon_product_id }) => addon_product_id,
  );
  const notFoundTicket = { description: "", amount: 0 };
  const hooplaItems: object[] = [];
  const orgItems: object[] = [];
  Object.entries(ticketInvoiceLines).forEach(([ticketTypeId, tls]) => {
    const description = (tls![0] || notFoundTicket).description;
    const tfls = ticketFeeInvoiceLines[ticketTypeId] || [notFoundTicket];
    const ticket_price = (tls![0] || notFoundTicket).amount / 100;
    const price = ticket_price + tfls[0].amount / 100;
    hooplaItems.push({
      item_id: ticketTypeId,
      item_name: description,
      item_category: "Tickets + Ticket Fee",
      price: price,
      quantity: tls!.length || 0,
      affiliation: window.location.host.split(".")[0], // organization identifier
    });

    if (
      orgTracker ||
      identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
    ) {
      orgItems.push({
        item_id: ticketTypeId,
        item_name: description,
        item_category: "Tickets",
        price: ticket_price,
        quantity: tls!.length || 0,
      });
    }
  });

  Object.entries(addonLines).forEach(([addonProductId, tls]) => {
    const description = (tls![0] || notFoundTicket).description;
    const tfls = addonFeeLines[addonProductId] || [notFoundTicket];
    const ticket_price = (tls![0] || notFoundTicket).amount / 100;
    const price = ticket_price + tfls[0].amount / 100;
    hooplaItems.push({
      item_id: addonProductId,
      item_name: description,
      item_category: "Addons + Addon Fee",
      price: price,
      quantity: tls!.length || 0,
      affiliation: window.location.host.split(".")[0], // organization identifier
    });

    if (
      orgTracker ||
      identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
    ) {
      orgItems.push({
        item_id: addonProductId,
        item_name: description,
        item_category: "Addons",
        price: ticket_price,
        quantity: tls!.length || 0,
      });
    }
  });

  const hooplaTransaction = {
    value:
      lines
        .filter(
          (il) =>
            isTicketInvoiceLineSpec(il) ||
            isTicketFeeInvoiceLineSpec(il) ||
            isAddonFeeInvoiceLineSpec(il) ||
            isAddonProductInvoiceLineSpec(il),
        )
        .reduce((acc, il) => acc + il.amount, 0) / 100,
    items: hooplaItems,
    currency: window.ORGANIZATION_CURRENCY,
  };

  pushToDataLayer({
    dataLayer: {
      event: "begin_checkout",
      ga4_tracking_id: window.ENVIRONMENT.__HOOPLA_GA4__,
      ...hooplaTransaction,
    },
  });

  if (
    orgTracker ||
    identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
  ) {
    const transaction = {
      value:
        lines
          .filter((il) => isTicketInvoiceLineSpec(il) || isAddonProductInvoiceLineSpec(il))
          .reduce((acc, il) => acc + il.amount, 0) / 100,
      items: orgItems,
      currency: window.ORGANIZATION_CURRENCY,
    };

    pushToDataLayer({
      dataLayer: {
        event: "begin_checkout",
        ga4_tracking_id: window.ORGANIZATION_GOOGLE_ANALYTICS,
        ...transaction,
      },
    });
  }

  if (pixelInitialized) {
    const c: { id: string; item_price: number; quantity: number }[] = [];
    Object.values(ticketInvoiceLines).forEach((tls) => {
      const description = (tls![0] || notFoundTicket).description;
      const ticket_price = (tls![0] || notFoundTicket).amount / 100;
      c.push({
        id: description,
        item_price: ticket_price,
        quantity: tls!.length || 0,
      });
    });
    Object.values(addonLines).forEach((als) => {
      const description = (als![0] || notFoundTicket).description;
      const ticket_price = (als![0] || notFoundTicket).amount / 100;
      c.push({
        id: description,
        item_price: ticket_price,
        quantity: als!.length || 0,
      });
    });
    pushToDataLayer({
      dataLayer: {
        fb_pixel_id: window.ORGANIZATION_FB_PIXEL,
        event: "begin_checkout_fbp",
        value:
          lines
            .filter((il) => isTicketInvoiceLineSpec(il) || isAddonProductInvoiceLineSpec(il))
            .reduce((sum, il) => sum + il.amount, 0) / 100,
        currency: window.ORGANIZATION_CURRENCY,
        contents: c,
      },
    });
  }
};

export const pageView = () => {
  if (
    orgTracker ||
    identifiersWithTrackers.includes(window.location.host.split(".")[0].toLowerCase())
  ) {
    pushToDataLayer({
      dataLayer: {
        event: "sales_pageview",
        enhanced_measurement: window.ORGANIZATION_ENHANCED_MEASUREMENT === "true",
        ga4_tracking_id: window.ORGANIZATION_GOOGLE_ANALYTICS,
      },
    });
  }

  if (pixelInitialized) {
    pushToDataLayer({
      dataLayer: {
        event: "sales_pageview_fbp",
        fb_pixel_id: window.ORGANIZATION_FB_PIXEL,
      },
    });
  }
};
